import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { selectedAddressAtom } from '../atoms/accountAtoms';
import { maxAddressManagersPhonesAtom, openInfoModalAtom } from '../atoms/globalAtoms';
import convertPhoneNumber from '../helpers/convertPhoneNumber';
import sendRequest from '../helpers/sendRequest';
import IntegrationServices from '../services/IntegrationServices';

const formatManagersPhoneForInput = (phone) => convertPhoneNumber(String(phone ?? ''));

const UseIntegrationAdditionalPhones = () => {
  const [selectedAddress] = useAtom(selectedAddressAtom);
  const [maxAdditionalPhones] = useAtom(maxAddressManagersPhonesAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);

  const [additionalPhones, setAdditionalPhones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAdditionalPhones = useCallback(async (addressId) => {
    if (!addressId) {
      setAdditionalPhones([]);
      return;
    }

    const data = await sendRequest({
      request: IntegrationServices.getAddressManagersPhones,
      payload: { addressId },
      warnErrorText: 'while getting address managers phones',
      setLoading,
    });

    const maxPhones = Number.isFinite(Number(maxAdditionalPhones)) && Number(maxAdditionalPhones) > 0
      ? Number(maxAdditionalPhones)
      : 0;

    if (data?.status !== 'ok' || maxPhones <= 0) {
      setAdditionalPhones([]);
      return;
    }

    const phones = Array.isArray(data.data?.managers_phones) ? data.data.managers_phones : [];
    setAdditionalPhones(
      phones.map(formatManagersPhoneForInput).slice(0, maxPhones),
    );
  }, [maxAdditionalPhones]);

  useEffect(() => {
    loadAdditionalPhones(selectedAddress?.id);
  }, [selectedAddress?.id, loadAdditionalPhones]);

  const setAdditionalPhoneAt = (index) => (ev) => {
    const next = [...additionalPhones];
    next[index] = typeof ev?.target?.value === 'string' ? ev.target.value : ev;
    setAdditionalPhones(next);
  };

  const onAddAdditionalPhoneClick = () => {
    if (!maxAdditionalPhones || additionalPhones.length >= maxAdditionalPhones) return;
    setAdditionalPhones([...additionalPhones, '']);
  };

  const onRemoveAdditionalPhoneClick = (index) => () => {
    setAdditionalPhones(additionalPhones.filter((_, i) => i !== index));
  };

  const onSaveClick = async () => {
    if (!selectedAddress?.id) return;

    const data = await sendRequest({
      request: IntegrationServices.saveAddressManagersPhones,
      payload: {
        addressId: selectedAddress.id,
        managersPhones: additionalPhones,
      },
      warnErrorText: 'while saving address managers phones',
      setLoading: setSaving,
    });

    if (data?.status !== 'ok') {
      openInfoModal({
        title: 'Ошибка',
        text: data?.message || 'Не удалось сохранить номера',
      });
      return;
    }

    const savedPhones = (Array.isArray(data.data?.managers_phones) ? data.data.managers_phones : [])
      .map(formatManagersPhoneForInput)
      .slice(0, maxAdditionalPhones);

    setAdditionalPhones(savedPhones);
    openInfoModal({
      title: 'Готово',
      text: 'Номера телефонов сохранены',
    });
  };

  return {
    values: {
      additionalPhones,
      maxAdditionalPhones,
      loading,
      saving,
    },
    events: {
      setAdditionalPhoneAt,
      onAddAdditionalPhoneClick,
      onRemoveAdditionalPhoneClick,
      onSaveClick,
    },
  };
};

export default UseIntegrationAdditionalPhones;
